<template>
  <div class="ss-wrap" :class="`ss-wrap--${accentKind}`" @focusin="onFocusIn" @focusout="onFocusOut">
    <div class="ss-shell" :class="{ 'ss-shell--active': focused || text }">
      <div class="ss-input-row">
        <span class="ss-icon-chip" aria-hidden="true">
          <svg class="ss-ico" viewBox="0 0 24 24"><path :d="headIcon" /></svg>
        </span>
        <input
          ref="input"
          v-model="text"
          class="ss-input"
          :placeholder="placeholder"
          :aria-label="placeholder"
          autocomplete="off"
          @keydown.enter.prevent="onEnter"
          @keydown.esc="onEsc"
          @keydown.down="onArrow($event, 1)"
          @keydown.up="onArrow($event, -1)"
        />
        <span v-if="text" class="ss-intent-badge">{{ intentLabel }}</span>
      </div>

      <transition name="ss-card">
        <div
          v-if="showCard"
          class="ss-card"
          :class="`ss-card--${effectiveIntent.toLowerCase()}`"
          @mousedown="onCardMouseDown"
        >
          <!-- What was read as a teammate / patient / date; clicking a word jumps to its control. -->
          <div v-if="readingSegments" class="ss-reading" data-test="ss-reading">
            <span v-for="(seg, i) in readingSegments" :key="i">
              <button
                v-if="seg.type !== 'text'"
                type="button"
                class="ss-reading__chip"
                :class="`ss-reading__chip--${seg.type}`"
                :title="readingTitle(seg)"
                @click="onSegmentClick(seg)"
              >{{ seg.text }}</button>
              <template v-else>{{ seg.text }}</template>
            </span>
          </div>

          <!-- DISAMBIGUATE: a capitalized name that matches nobody -->
          <template v-if="showDisambiguate">
            <div class="ss-card__header">
              <svg class="ss-ico ss-ico--accent" viewBox="0 0 24 24"><path :d="icons.ask" /></svg>
              <span class="ss-card__title">"{{ resolved.unmatchedName }}" — who do you mean?</span>
            </div>
            <div class="ss-disambiguate-btns">
              <button type="button" class="ss-disambiguate-btn" @click="chooseDisambiguation('TEAM')">
                <span class="ss-disambiguate-btn__icon ss-disambiguate-btn__icon--team">
                  <svg class="ss-ico" viewBox="0 0 24 24"><path :d="icons.team" /></svg>
                </span>
                <span class="ss-disambiguate-btn__text">
                  <span class="ss-disambiguate-btn__label">Assign to a teammate</span>
                  <span class="ss-disambiguate-btn__sub">They get notified</span>
                </span>
              </button>
              <button type="button" class="ss-disambiguate-btn" @click="chooseDisambiguation('PATIENT')">
                <span class="ss-disambiguate-btn__icon ss-disambiguate-btn__icon--patient">
                  <svg class="ss-ico" viewBox="0 0 24 24"><path :d="icons.patient" /></svg>
                </span>
                <span class="ss-disambiguate-btn__text">
                  <span class="ss-disambiguate-btn__label">Tag a patient</span>
                  <span class="ss-disambiguate-btn__sub">Linked to their profile</span>
                </span>
              </button>
            </div>
            <div class="ss-card__footer">
              <span class="ss-card__spacer"></span>
              <button type="button" class="btn btn-link btn-sm" @click="reset">Cancel</button>
            </div>
          </template>

          <!-- REMINDER -->
          <template v-else-if="effectiveIntent === 'REMINDER'">
            <div class="ss-card__header">
              <svg class="ss-ico ss-ico--accent" viewBox="0 0 24 24"><path :d="icons.reminder" /></svg>
              <span class="ss-card__title">When?</span>
            </div>
            <div class="ss-card__quick-dates">
              <button
                v-for="opt in quickDates()"
                :key="opt.label"
                class="btn btn-sm ss-quick-btn"
                :class="{ active: shownDue === opt.value }"
                type="button"
                @click="setDue(opt.value)"
              >{{ opt.label }}</button>
            </div>
            <div class="ss-card__custom-date">
              <label class="ss-label">Or pick a date</label>
              <input type="date" :value="shownDue" class="form-control form-control-sm" @input="setDue($event.target.value)" />
            </div>
          </template>

          <!-- TEAM -->
          <template v-else-if="effectiveIntent === 'TEAM'">
            <div class="ss-card__header">
              <svg class="ss-ico ss-ico--accent" viewBox="0 0 24 24"><path :d="icons.team" /></svg>
              <span class="ss-card__title">{{ teamTitle }}</span>
            </div>
            <div class="ss-card__employees">
              <button
                v-for="emp in orderedEmployees"
                :key="emp.EmployeeID"
                type="button"
                class="ss-emp-btn"
                :class="{
                  'ss-emp-btn--active': isNavActive('emp', emp),
                  'ss-emp-btn--selected': selectedAssigneeID === emp.EmployeeID,
                  'ss-emp-btn--suggested': selectedAssigneeID !== emp.EmployeeID && isCandidate(emp),
                }"
                :title="emp.Username"
                @click="toggleAssignee(emp.EmployeeID)"
              >
                <span class="ss-emp-btn__avatar">{{ empInitials(emp) }}</span>
                <span class="ss-emp-btn__name">{{ empLabel(emp) }}</span>
              </button>
              <div v-if="!activeEmployees.length" class="ss-patient-empty text-muted">No team members loaded</div>
            </div>
            <!-- Show patient search when a patient name was also detected in the same text -->
            <template v-if="patientSearch || resolved.patientHint">
              <div class="ss-card__header ss-card__header--secondary">
                <svg class="ss-ico ss-ico--accent" viewBox="0 0 24 24"><path :d="icons.patient" /></svg>
                <span class="ss-card__title">Also tag a patient?</span>
              </div>
              <div class="ss-card__patient-search">
                <input
                  v-model="patientSearch"
                  class="form-control form-control-sm"
                  placeholder="Search by name…"
                  aria-label="Search patients by name"
                  autocomplete="off"
                  @input="patientSearchManual = true"
                  @keydown.stop
                />
                <div v-if="patientResults.length" class="ss-patient-list">
                  <button
                    v-for="pt in patientResults"
                    :key="pt.PatNum"
                    type="button"
                    class="ss-patient-btn"
                    :class="{
                      'ss-patient-btn--active': isNavActive('pat', pt),
                      'ss-patient-btn--selected': selectedPatient && selectedPatient.PatNum === pt.PatNum,
                    }"
                    @click="selectPatient(pt)"
                  >
                    <span class="ss-patient-btn__name">{{ pt.LName }}, {{ pt.FName }}</span>
                    <span v-if="isRecentPatient(pt)" class="ss-recent-tag">Recent</span>
                    <span class="ss-patient-btn__dob text-muted">DOB: {{ dobLabel(pt) }}</span>
                  </button>
                </div>
                <div v-else-if="patientSearch.trim().length >= 2" class="ss-patient-empty text-muted">
                  No patients found
                </div>
              </div>
            </template>
            <div class="ss-card__row">
              <label class="ss-label">Due (optional)</label>
              <input type="date" :value="shownDue" class="form-control form-control-sm" @input="setDue($event.target.value)" />
            </div>
          </template>

          <!-- PATIENT -->
          <template v-else-if="effectiveIntent === 'PATIENT'">
            <div class="ss-card__header">
              <svg class="ss-ico ss-ico--accent" viewBox="0 0 24 24"><path :d="icons.patient" /></svg>
              <span class="ss-card__title">Which patient?</span>
            </div>
            <div class="ss-card__patient-search">
              <input
                v-model="patientSearch"
                class="form-control form-control-sm"
                placeholder="Search by name…"
                aria-label="Search patients by name"
                autocomplete="off"
                @input="patientSearchManual = true"
                @keydown.stop
              />
              <div v-if="patientResults.length" class="ss-patient-list">
                <button
                  v-for="pt in patientResults"
                  :key="pt.PatNum"
                  type="button"
                  class="ss-patient-btn"
                  :class="{
                    'ss-patient-btn--active': isNavActive('pat', pt),
                    'ss-patient-btn--selected': selectedPatient && selectedPatient.PatNum === pt.PatNum,
                  }"
                  @click="selectPatient(pt)"
                >
                  <span class="ss-patient-btn__name">{{ pt.LName }}, {{ pt.FName }}</span>
                  <span v-if="isRecentPatient(pt)" class="ss-recent-tag">Recent</span>
                  <span class="ss-patient-btn__dob text-muted">DOB: {{ dobLabel(pt) }}</span>
                </button>
              </div>
              <div v-else-if="patientSearch.trim().length >= 2" class="ss-patient-empty text-muted">
                No patients found
              </div>
            </div>
            <div class="ss-card__row">
              <label class="ss-label">Due (optional)</label>
              <input type="date" :value="shownDue" class="form-control form-control-sm" @input="setDue($event.target.value)" />
            </div>
          </template>

          <!-- GENERIC -->
          <template v-else>
            <div class="ss-card__header">
              <svg class="ss-ico ss-ico--accent" viewBox="0 0 24 24"><path :d="icons.generic" /></svg>
              <span class="ss-card__title">Quick add</span>
            </div>
            <div class="ss-card__row">
              <label class="ss-label">Due (optional)</label>
              <input type="date" :value="shownDue" class="form-control form-control-sm" @input="setDue($event.target.value)" />
            </div>
          </template>

          <template v-if="!showDisambiguate">
            <!-- Optional extras shared by every card, so no card changes shape. -->
            <div class="ss-extras" role="group" aria-label="Repeat and attach">
              <button
                v-for="x in extraButtons"
                :key="x.key"
                type="button"
                class="ss-extra-btn"
                :class="{ 'ss-extra-btn--open': openExtra === x.key, 'ss-extra-btn--set': x.set }"
                :data-test="`ss-extra-${x.key}`"
                :aria-expanded="openExtra === x.key ? 'true' : 'false'"
                @click="toggleExtra(x.key)"
              >
                <svg class="ss-ico ss-ico--sm" viewBox="0 0 24 24"><path :d="icons[x.icon]" /></svg>
                {{ x.label }}
              </button>
            </div>

            <div v-if="openExtra" class="ss-extra-panel">
              <template v-if="openExtra === 'repeat'">
                <label class="ss-label" for="ss-repeat-select">Repeat</label>
                <select
                  id="ss-repeat-select"
                  class="form-control form-control-sm"
                  data-test="ss-repeat-select"
                  :value="repeatSelectValue"
                  @change="onRepeatChange($event.target.value)"
                >
                  <option v-for="o in repeatMenu" :key="o.key" :value="o.key">{{ o.label }}</option>
                </select>
              </template>

              <template v-else-if="openExtra === 'wiki'">
                <input
                  v-model="wikiQuery"
                  class="form-control form-control-sm"
                  data-test="ss-wiki-search"
                  placeholder="Search wiki pages…"
                  aria-label="Search wiki pages"
                  autocomplete="off"
                  @keydown.enter.prevent
                  @keydown.esc.stop="closeExtra"
                />
                <div v-if="wikiState === 'loading'" class="ss-patient-empty text-muted">Loading wiki pages…</div>
                <div v-else-if="wikiState === 'error'" class="ss-patient-empty text-muted">Could not load wiki pages</div>
                <div v-else-if="!wikiPages.length" class="ss-patient-empty text-muted">No wiki pages</div>
                <div v-else-if="!wikiResults.length" class="ss-patient-empty text-muted">No pages match</div>
                <div v-else class="ss-patient-list">
                  <button
                    v-for="pg in wikiResults"
                    :key="pg.WikiPageID"
                    type="button"
                    class="ss-patient-btn"
                    :class="{ 'ss-patient-btn--selected': wikiIDs.includes(pg.WikiPageID) }"
                    data-test="ss-wiki-row"
                    @click="toggleWiki(pg.WikiPageID)"
                  >
                    <span class="ss-patient-btn__name">{{ pg.Name }}</span>
                    <svg v-if="wikiIDs.includes(pg.WikiPageID)" class="ss-ico ss-ico--sm ss-ico--accent" viewBox="0 0 24 24"><path :d="icons.check" /></svg>
                  </button>
                </div>
              </template>

              <template v-else-if="openExtra === 'tags'">
                <input
                  v-model="tagQuery"
                  class="form-control form-control-sm"
                  data-test="ss-tag-input"
                  placeholder="Add a tag…"
                  aria-label="Add a tag"
                  autocomplete="off"
                  @keydown.enter.prevent="commitTag"
                  @keydown.esc.stop="closeExtra"
                />
                <div v-if="tagMatches.suggestions.length || tagMatches.create" class="ss-patient-list">
                  <button
                    v-for="tg in tagMatches.suggestions"
                    :key="tg"
                    type="button"
                    class="ss-patient-btn"
                    data-test="ss-tag-suggestion"
                    @click="addTag(tg)"
                  >
                    <span class="ss-patient-btn__name">{{ tg }}</span>
                  </button>
                  <button
                    v-if="tagMatches.create"
                    type="button"
                    class="ss-patient-btn"
                    data-test="ss-tag-create"
                    @click="addTag(tagMatches.create)"
                  >
                    <span class="ss-patient-btn__name">Create “{{ tagMatches.create }}”</span>
                  </button>
                </div>
              </template>

              <template v-else-if="openExtra === 'subtasks'">
                <ul v-if="subtasks.length" class="ss-sublist">
                  <li v-for="(st, i) in subtasks" :key="st.id" class="ss-sublist__row" data-test="ss-subtask-row">
                    <span class="ss-sublist__text">{{ st.content }}</span>
                    <button type="button" class="ss-row__x" :aria-label="`Remove ${st.content}`" @click="removeSubtask(i)">
                      <svg viewBox="0 0 24 24"><path :d="icons.x" /></svg>
                    </button>
                  </li>
                </ul>
                <input
                  v-model="subtaskDraft"
                  class="form-control form-control-sm"
                  data-test="ss-subtask-input"
                  placeholder="Add a subtask, press Enter…"
                  aria-label="Add a subtask"
                  autocomplete="off"
                  @keydown.enter.prevent="commitSubtask"
                  @keydown.esc.stop="closeExtra"
                />
              </template>
            </div>

            <div v-if="hasPills" class="ss-preview" aria-label="This task will be added with">
              <span v-if="assigneeEmp" class="ss-pill">
                <span class="ss-pill__avatar">{{ empInitials(assigneeEmp) }}</span>
                {{ empFirstName(assigneeEmp) }}
              </span>
              <span v-if="selectedPatient" class="ss-pill">
                <svg class="ss-ico ss-ico--sm" viewBox="0 0 24 24"><path :d="icons.patient" /></svg>
                {{ selectedPatient.FName }} {{ selectedPatient.LName }}
              </span>
              <span v-if="dueLabel" class="ss-pill">
                <svg class="ss-ico ss-ico--sm" viewBox="0 0 24 24"><path :d="icons.reminder" /></svg>
                {{ dueLabel }}
              </span>
              <span v-if="repeatPlan" class="ss-pill" data-test="ss-pill-repeat">
                <svg class="ss-ico ss-ico--sm" viewBox="0 0 24 24"><path :d="icons.repeat" /></svg>
                {{ repeatPlan.summary }}
                <button type="button" class="ss-pill__x" aria-label="Don’t repeat" @click="repeatPick = 'none'">
                  <svg viewBox="0 0 24 24"><path :d="icons.x" /></svg>
                </button>
              </span>
              <span v-for="pg in attachedWiki" :key="`w${pg.WikiPageID}`" class="ss-pill" data-test="ss-pill-wiki">
                <svg class="ss-ico ss-ico--sm" viewBox="0 0 24 24"><path :d="icons.wiki" /></svg>
                {{ pg.Name }}
                <button type="button" class="ss-pill__x" :aria-label="`Detach ${pg.Name}`" @click="toggleWiki(pg.WikiPageID)">
                  <svg viewBox="0 0 24 24"><path :d="icons.x" /></svg>
                </button>
              </span>
              <span v-for="tg in tags" :key="`t${tg}`" class="ss-pill" data-test="ss-pill-tag">
                <svg class="ss-ico ss-ico--sm" viewBox="0 0 24 24"><path :d="icons.tag" /></svg>
                {{ tg }}
                <button type="button" class="ss-pill__x" :aria-label="`Remove tag ${tg}`" @click="removeTag(tg)">
                  <svg viewBox="0 0 24 24"><path :d="icons.x" /></svg>
                </button>
              </span>
              <span v-if="subtasks.length" class="ss-pill" data-test="ss-pill-subtasks">
                <svg class="ss-ico ss-ico--sm" viewBox="0 0 24 24"><path :d="icons.subtasks" /></svg>
                {{ subtasks.length }} {{ subtasks.length === 1 ? 'subtask' : 'subtasks' }}
              </span>
            </div>

            <div v-if="error" class="ss-error text-danger">{{ error }}</div>

            <div class="ss-card__footer">
              <span class="ss-hint">
                <template v-if="navTarget"><kbd>↑</kbd><kbd>↓</kbd> to choose</template>
                <template v-else><kbd>Esc</kbd> to clear</template>
              </span>
              <span class="ss-card__spacer"></span>
              <button type="button" class="btn btn-link btn-sm" @click="reset">Cancel</button>
              <button type="button" class="btn btn-primary btn-sm" :disabled="saving || !text.trim()" @click="create">
                {{ primaryLabel }}
              </button>
            </div>
          </template>

        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import moment from 'moment-timezone';
import simpleParser from '@/utils/chrono-simple';
import { classifyTaskIntent, findDetectedSpans, resolveNames } from '@/utils/shapeshiftClassifier';
import { buildNewTask } from '@/utils/taskDraft';
import { ensurePatientTagged } from '@/utils/taskPatients';
import { buildEmployeeBadgeHTML } from '@/utils/voiceTask';
import { formatOdDate } from '@/utils/patientPanel/format';
import {
  repeatOptions, repeatSummary, skipExcludedDays, tagChoices, cleanTag, subtasksPatch,
} from '@/utils/tasks/drawerModel';

// task.content is rendered with v-html, so the typed text is entity-escaped exactly as the
// contenteditable composer stores it; only the badges built by the helpers below are markup.
function escapeText(value) {
  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

// 24x24 stroke icons (Feather paths), one per card type.
const ICONS = Object.freeze({
  plus: 'M12 5v14M5 12h14',
  reminder: 'M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2zM16 2v4M8 2v4M3 10h18',
  team: 'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75',
  patient: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M16 13H8M16 17H8M10 9H8',
  generic: 'M22 11.08V12a10 10 0 1 1-5.93-9.14M22 4L12 14.01l-3-3',
  ask: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3M12 17h.01',
  repeat: 'M17 1l4 4-4 4M3 11V9a4 4 0 0 1 4-4h14M7 23l-4-4 4-4M21 13v2a4 4 0 0 1-4 4H3',
  wiki: 'M4 19.5A2.5 2.5 0 0 1 6.5 17H20M4 19.5A2.5 2.5 0 0 0 6.5 22H20V2H6.5A2.5 2.5 0 0 0 4 4.5v15z',
  tag: 'M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82zM7 7h.01',
  subtasks: 'M9 11l3 3L22 4M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11',
  x: 'M18 6L6 18M6 6l12 12',
  check: 'M20 6L9 17l-5-5',
});

// Same shape as the Task drawer's subtask ids (TaskDrawer.vue newSubtaskId / TodoDetails generateSubtaskId).
function newSubtaskId() {
  return `st_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

const WIKI_LIST_CAP = 50;

// "Recent" picks live only in this browser, per site + user, as bare IDs (no names, no DOB): the
// names are looked up from the loaded lists. A rolling window: the newest few, older ones fall off.
const RECENT_CAP = { emp: 5, pat: 8 };
const NOT_RECENT = 1e9;

function recentsKey(siteID, userID) {
  return `teamio_shapeshift_recents:${siteID}:${userID}`;
}

function readRecents(key) {
  const empty = { emp: [], pat: [] };
  try {
    const raw = JSON.parse(window.localStorage.getItem(key));
    if (!raw || typeof raw !== 'object') return empty;
    return {
      emp: Array.isArray(raw.emp) ? raw.emp.slice(0, RECENT_CAP.emp) : [],
      pat: Array.isArray(raw.pat) ? raw.pat.slice(0, RECENT_CAP.pat) : [],
    };
  } catch (e) {
    return empty;
  }
}

function pushRecent(list, id, cap) {
  return [id].concat(list.filter(x => x !== id)).slice(0, cap);
}

function nameParts(emp) {
  return String((emp && (emp.Name || emp.Username)) || '').trim().split(/\s+/).filter(Boolean);
}

export default {
  name: 'ShapeShiftTaskInput',

  props: {
    placeholder: { type: String, default: 'What needs to happen?' },
    maxSortId: { type: Number, default: 0 },
    // Start of the day a task is due when no date is typed or picked (the Tasks page: today, tomorrow on Upcoming).
    defaultDue: { type: Date, default: null },
  },

  data() {
    return {
      text: '',
      focused: false,
      dueDate: '',
      lastAutoDue: '', // the date the text last set; the due date follows the text only while it still equals this
      assigneeID: null, // the user's own pick, used once assigneeManual is true
      assigneeManual: false, // false: the teammate follows the text; true: the user clicked, the text no longer decides
      selectedPatient: null,
      patientSearch: '',
      patientSearchManual: false, // true once the user types in the patient box
      saving: false,
      error: '',
      disambiguateChoice: null, // null | 'TEAM' | 'PATIENT' — set when user resolves the ambiguity prompt
      icons: ICONS,
      activeIndex: -1, // the row ↑/↓ is on inside navTarget.items; -1 = none, so Enter still saves
      recents: { emp: [], pat: [] }, // newest first: EmployeeIDs / PatNums this user picked lately
      // Repeat + Attach: one optional row shared by every card.
      openExtra: '', // '' | 'repeat' | 'wiki' | 'tags' | 'subtasks' — the one panel showing
      repeatPick: null, // null: the repeat follows the words; 'none': switched off; otherwise a repeatOptions() key
      wikiIDs: [],
      wikiQuery: '',
      wikiPages: [], // the site's pages, read once when the picker is first opened
      wikiState: '', // '' | 'loading' | 'loaded' | 'error'
      tags: [],
      tagQuery: '',
      ownsTagListener: false, // true only if this component started the site-tags listener, so only it stops it
      subtasks: [], // already in saved shape: { id, content, status, sortOrder, createdAt }
      subtaskDraft: '',
    };
  },

  created() {
    this.recents = readRecents(recentsKey(this.siteID, this.userID));
  },

  beforeDestroy() {
    if (this.ownsTagListener) this.$store.dispatch('tasks/unsubscribeSiteTaskTags');
  },

  computed: {
    intent() {
      return classifyTaskIntent(this.text);
    },
    // The due date written in the text, read by the same parser the main Tasks composer uses.
    // { ymd: 'YYYY-MM-DD', phrase: 'day after tomorrow', recurrence } or null. `recurrence` is the
    // detectedDateInfo the main composer hands buildNewTask when the words repeat ("every Monday"), else null.
    textDate() {
      if (!this.text.trim()) return null;
      let parsed = null;
      try { parsed = simpleParser.parse(this.text); } catch (e) { return null; }
      if (!parsed || !(parsed.date instanceof Date) || isNaN(parsed.date.getTime())) return null;
      const recurrence = parsed.isRecurring ? {
        hasDate: true,
        displayText: parsed.text || '',
        date: parsed.date,
        isRecurring: true,
        frequency: parsed.frequency,
        daysOfWeek: parsed.daysOfWeek,
        dayOfMonth: parsed.dayOfMonth,
        repeatOnMonth: parsed.repeatOnMonth,
        isLastDayOfMonth: parsed.repeatOnLastDayOfMonth,
      } : null;
      return { ymd: moment(parsed.date).format('YYYY-MM-DD'), phrase: parsed.text || '', recurrence };
    },
    // The repeat fields buildNewTask derives from the typed words, or null when the words do not repeat.
    textRepeatTask() {
      const recurrence = this.textDate && this.textDate.recurrence;
      if (!recurrence) return null;
      try {
        return buildNewTask({
          contentHTML: '',
          siteID: this.siteID,
          userID: this.userID,
          userName: this.userName,
          timezone: this.timezone,
          maxSortID: 0,
          detectedDateInfo: recurrence,
        }).task;
      } catch (e) {
        return null;
      }
    },
    // The Repeat menu is built around the due date picked (else today), exactly as the Task drawer's is.
    defaultYmd() {
      if (!(this.defaultDue instanceof Date) || isNaN(this.defaultDue.getTime())) return '';
      return moment.tz(this.defaultDue, this.timezone).format('YYYY-MM-DD');
    },
    // The day the task is due so far: the picked or typed date, else the page's default day.
    shownDue() {
      return this.effectiveDue || this.defaultYmd;
    },
    repeatMenu() {
      const opts = repeatOptions(this.dueDate || this.defaultYmd || this.todayYmd()).map(o => ({ key: o.key, label: o.label }));
      if (this.textRepeatTask) opts.unshift({ key: 'text', label: `Same as typed: ${repeatSummary(this.textRepeatTask)}` });
      return opts;
    },
    repeatSelectValue() {
      return this.repeatPick || (this.textRepeatTask ? 'text' : 'none');
    },
    // What the task will repeat as, or null. source 'text': the words decide (saved through buildNewTask,
    // like the main composer); 'menu': a menu pick, saved as that option's fields.
    repeatPlan() {
      if (this.repeatPick === 'none') return null;
      if (!this.repeatPick) {
        return this.textRepeatTask ? { source: 'text', summary: repeatSummary(this.textRepeatTask) } : null;
      }
      const opt = repeatOptions(this.dueDate || this.defaultYmd || this.todayYmd()).find(o => o.key === this.repeatPick);
      if (!opt || !opt.fields.repeat) return null;
      return { source: 'menu', summary: opt.label, fields: opt.fields };
    },
    // The day the task will really be due: "Every weekday" on a Saturday starts on Monday.
    effectiveDue() {
      const plan = this.repeatPlan;
      if (plan && plan.source === 'menu') return skipExcludedDays(plan.fields, this.dueDate || this.defaultYmd || this.todayYmd());
      return this.dueDate;
    },
    siteTags() {
      const tasks = this.$store.state.tasks;
      return tasks && Array.isArray(tasks.siteTaskTags) ? tasks.siteTaskTags : [];
    },
    tagChoicesAll() {
      return tagChoices(this.siteTags, this.tags, this.tagQuery, 1000);
    },
    tagMatches() {
      return { suggestions: this.tagChoicesAll.suggestions.slice(0, 8), create: this.tagChoicesAll.create };
    },
    wikiResults() {
      const tokens = this.wikiQuery.toLowerCase().split(/\s+/).filter(Boolean);
      return this.wikiPages
        .filter(p => tokens.every(t => p.Name.toLowerCase().includes(t)))
        .slice(0, WIKI_LIST_CAP);
    },
    attachedWiki() {
      return this.wikiIDs.map(id => this.wikiPages.find(p => p.WikiPageID === id)).filter(Boolean);
    },
    extraButtons() {
      const n = (label, count) => (count ? `${label} · ${count}` : label);
      return [
        { key: 'repeat', icon: 'repeat', label: 'Repeat', set: !!this.repeatPlan },
        { key: 'wiki', icon: 'wiki', label: n('Wiki', this.wikiIDs.length), set: this.wikiIDs.length > 0 },
        { key: 'tags', icon: 'tag', label: n('Tags', this.tags.length), set: this.tags.length > 0 },
        { key: 'subtasks', icon: 'subtasks', label: n('Subtasks', this.subtasks.length), set: this.subtasks.length > 0 },
      ];
    },
    hasPills() {
      return !!(this.assigneeEmp || this.selectedPatient || this.dueLabel || this.repeatPlan
        || this.attachedWiki.length || this.tags.length || this.subtasks.length);
    },
    // Who the text mentions, matched against the loaded employees and patients (any letter case).
    resolved() {
      return resolveNames(
        this.text,
        this.activeEmployees,
        this.$store.state.customer.patients,
        { preferPatient: this.intent === 'PATIENT', dateText: this.textDate ? this.textDate.phrase : '' },
      );
    },
    hasEmployeeMatch() {
      return !!this.resolved.employee || this.resolved.employeeCandidates.length > 0;
    },
    // Derived, never copied into state: the text's match until the user clicks a teammate themselves.
    selectedAssigneeID() {
      if (this.assigneeManual) return this.assigneeID;
      return this.resolved.employee ? this.resolved.employee.EmployeeID : null;
    },
    // Ask "teammate or patient?" only for a capitalized name that matches nobody.
    showDisambiguate() {
      return this.intent === 'GENERIC' && !this.disambiguateChoice && !!this.resolved.unmatchedName;
    },
    // The intent that actually drives the card. A matched teammate wins (employees are checked first);
    // a matched patient turns a plain/reminder task into a patient task.
    effectiveIntent() {
      if (this.disambiguateChoice) return this.disambiguateChoice;
      if (this.hasEmployeeMatch) return 'TEAM';
      if (this.resolved.patientHint && this.intent !== 'TEAM') return 'PATIENT';
      return this.intent;
    },
    teamTitle() {
      const cands = this.resolved.employeeCandidates;
      if (cands.length > 1) {
        const firsts = new Set(cands.map(e => this.empFirstName(e).toLowerCase()));
        return firsts.size === 1 ? `Which ${this.empFirstName(cands[0])}?` : 'Which teammate?';
      }
      return 'Who should handle this?';
    },
    // Teammates the text fits come first (so the choice is one click away), then the ones picked
    // lately, then everyone else in their usual order.
    orderedEmployees() {
      const byRecent = list => {
        const rank = e => { const i = this.recents.emp.indexOf(e.EmployeeID); return i === -1 ? NOT_RECENT : i; };
        return list.map((e, i) => ({ e, i })).sort((a, b) => (rank(a.e) - rank(b.e)) || (a.i - b.i)).map(x => x.e);
      };
      const cands = this.resolved.employeeCandidates;
      if (!cands.length) return byRecent(this.activeEmployees);
      const ids = new Set(cands.map(e => e.EmployeeID));
      return byRecent(this.activeEmployees.filter(e => ids.has(e.EmployeeID)))
        .concat(byRecent(this.activeEmployees.filter(e => !ids.has(e.EmployeeID))));
    },
    // The detected words, marked, for the line at the top of the card. null when nothing was detected.
    readingSegments() {
      if (this.showDisambiguate || !this.text.trim()) return null;
      const segs = findDetectedSpans(this.text, this.resolved, this.textDate ? this.textDate.phrase : '');
      return segs.some(s => s.type !== 'text') ? segs : null;
    },
    intentLabel() {
      return { REMINDER: 'Reminder', TEAM: 'Team task', PATIENT: 'Patient task', GENERIC: 'Task' }[this.effectiveIntent];
    },
    // What ↑/↓ in the main input walks: first an unresolved "Which Saumil?", then the patient list.
    // The input keeps focus the whole time, so the user can keep typing between arrow presses.
    navTarget() {
      if (this.showDisambiguate) return null;
      if (this.effectiveIntent === 'TEAM' && this.resolved.employeeCandidates.length > 1 && !this.selectedAssigneeID) {
        return { kind: 'emp', items: this.orderedEmployees.filter(e => this.isCandidate(e)) };
      }
      if ((this.effectiveIntent === 'TEAM' || this.effectiveIntent === 'PATIENT') && !this.selectedPatient && this.patientResults.length) {
        return { kind: 'pat', items: this.patientResults };
      }
      return null;
    },
    // Which look is showing: drives the icon chip and the accent colour. Blue ("idle") until something is typed.
    accentKind() {
      if (!this.text.trim()) return 'idle';
      return this.showDisambiguate ? 'idle' : this.effectiveIntent.toLowerCase();
    },
    headIcon() {
      if (!this.text.trim()) return ICONS.plus;
      return this.showDisambiguate ? ICONS.ask : ICONS[this.effectiveIntent.toLowerCase()];
    },
    dueLabel() {
      if (!this.shownDue) return '';
      const m = moment(this.shownDue, 'YYYY-MM-DD', true);
      if (!m.isValid()) return '';
      if (m.isSame(moment(), 'day')) return 'Today';
      if (m.isSame(moment().add(1, 'days'), 'day')) return 'Tomorrow';
      return m.format('ddd, MMM D');
    },
    showCard() {
      return (this.focused || this.saving) && this.text.trim().length > 0;
    },
    // Same people the real @ picker offers: customer/activeEmployees is `Active === 1`.
    // Employee rows carry `Name` (not FirstName/LastName).
    activeEmployees() {
      const emps = this.$store.state.customer.employees || [];
      return emps.filter(e => e && e.Active === 1);
    },
    assigneeEmp() {
      if (this.effectiveIntent !== 'TEAM' || !this.selectedAssigneeID) return null;
      return this.activeEmployees.find(e => e.EmployeeID === this.selectedAssigneeID) || null;
    },
    assigneeName() {
      return this.assigneeEmp ? this.empFirstName(this.assigneeEmp) : '';
    },
    patientResults() {
      if (this.patientSearch.trim().length < 2) return [];
      // "Smith, John" and "john smith" both match: every typed word must appear in "LName FName".
      const tokens = this.patientSearch.toLowerCase().split(/[\s,]+/).filter(Boolean);
      const patients = this.$store.state.customer.patients || [];
      const recent = this.recents.pat;
      const recentHits = [];
      const rest = [];
      for (const p of patients) {
        const rank = recent.indexOf(p.PatNum);
        if (rank === -1 && rest.length === 6) continue;
        const hay = `${p.LName || ''} ${p.FName || ''}`.toLowerCase();
        if (!tokens.every(t => hay.includes(t))) continue;
        if (rank === -1) rest.push(p);
        else recentHits.push({ p, rank });
      }
      // Patients picked lately jump ahead of the rest, newest first; the list stays at 6.
      return recentHits.sort((a, b) => a.rank - b.rank).map(x => x.p).concat(rest).slice(0, 6);
    },
    primaryLabel() {
      if (this.saving) return 'Adding…';
      if (this.effectiveIntent === 'TEAM' && this.assigneeName) return `Assign to ${this.assigneeName}`;
      if (this.effectiveIntent === 'PATIENT' && this.selectedPatient) {
        return `Add for ${this.selectedPatient.FName} ${this.selectedPatient.LName}`;
      }
      return 'Add Task';
    },
    siteID() { return this.$store.state.customer.siteID; },
    userID() { return this.$store.state.customer.userID; },
    userName() {
      const u = this.$store.state.customer.user;
      return u ? u.Name : null;
    },
    // state.customer.settings is an ARRAY of { Property, PropertyValue } rows (mixin.js allSettings),
    // not an object keyed by property. Same lookup + validity check as ToDo.vue::tkTimeZone.
    timezone() {
      const rows = this.$store.state.customer.settings;
      const row = Array.isArray(rows) ? rows.find(s => s && s.Property === 'GENERAL_TIMEZONE') : null;
      const tz = row && row.PropertyValue ? String(row.PropertyValue) : '';
      return tz && moment.tz.zone(tz) ? tz : null;
    },
  },

  watch: {
    text(val) {
      this.error = '';
      this.activeIndex = -1;
      // The due date follows the words, until the user picks one themselves.
      if (!this.dueDate || this.dueDate === this.lastAutoDue) {
        const auto = this.textDate ? this.textDate.ymd : '';
        this.dueDate = auto;
        this.lastAutoDue = auto;
      }
      // A cleared box closes the card, so whatever was attached in it goes too.
      if (!val.trim()) this.resetExtras();
    },
    // Keep the patient search box in step with the patient name in the text, until the user edits the box
    // or picks a patient.
    'resolved.patientHint'(hint) {
      if (!this.selectedPatient && !this.patientSearchManual) this.patientSearch = hint || '';
    },
    'resolved.unmatchedName'() {
      this.disambiguateChoice = null;
    },
    // Picks that belong to a card the user can no longer see must not ride along on the saved task.
    effectiveIntent(val) {
      if (val !== 'TEAM') {
        this.assigneeID = null;
        this.assigneeManual = false;
      }
      if (val !== 'TEAM' && val !== 'PATIENT') {
        this.selectedPatient = null;
        this.patientSearch = '';
        this.patientSearchManual = false;
      }
    },
    // Editing the search box after picking someone un-picks them, so the button label and the
    // patient tag written on the task can never disagree with what the box shows.
    patientSearch(val) {
      this.activeIndex = -1;
      if (this.selectedPatient && val !== this.patientLabel(this.selectedPatient)) {
        this.selectedPatient = null;
      }
    },
  },
  methods: {
    // Focus is tracked for the WHOLE widget (input + card). The card holds its own focusable
    // controls (date inputs, patient search); closing on the main input's blur would unmount them
    // the moment they are clicked.
    onFocusIn() {
      this.focused = true;
    },
    onFocusOut(e) {
      const next = e.relatedTarget;
      if (next && this.$el.contains(next)) return;
      this.focused = false;
    },
    // Clicking card chrome (labels, padding, buttons) must not pull focus out of the widget.
    onCardMouseDown(e) {
      const tag = e.target && e.target.tagName;
      if (tag !== 'INPUT' && tag !== 'SELECT' && tag !== 'TEXTAREA') e.preventDefault();
    },
    // With a row highlighted by ↑/↓, Enter picks it; the next Enter saves.
    onEnter() {
      const target = this.navTarget;
      const row = target && this.activeIndex >= 0 ? target.items[this.activeIndex] : null;
      if (row) {
        if (target.kind === 'emp') this.toggleAssignee(row.EmployeeID);
        else this.selectPatient(row);
        this.activeIndex = -1;
        return;
      }
      if (this.text.trim()) this.create();
    },
    onArrow(e, dir) {
      const target = this.navTarget;
      if (!target || !target.items.length) return;
      e.preventDefault();
      const n = target.items.length;
      this.activeIndex = this.activeIndex < 0 ? (dir > 0 ? 0 : n - 1) : (this.activeIndex + dir + n) % n;
      this.$nextTick(() => {
        const el = this.$el.querySelector('.ss-emp-btn--active, .ss-patient-btn--active');
        if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
      });
    },
    isNavActive(kind, item) {
      const target = this.navTarget;
      return !!target && target.kind === kind && this.activeIndex >= 0 && target.items[this.activeIndex] === item;
    },
    onEsc() {
      this.reset();
      this.$refs.input.blur();
    },
    chooseDisambiguation(kind) {
      this.disambiguateChoice = kind;
      if (kind === 'PATIENT' && this.resolved.unmatchedName) {
        this.patientSearch = this.resolved.unmatchedName;
      }
    },
    isCandidate(emp) {
      return this.resolved.employeeCandidates.some(c => c.EmployeeID === emp.EmployeeID);
    },
    toggleAssignee(empID) {
      const current = this.selectedAssigneeID;
      this.assigneeManual = true;
      this.assigneeID = current === empID ? null : empID;
    },
    patientLabel(pt) {
      return `${pt.LName}, ${pt.FName}`;
    },
    selectPatient(pt) {
      this.selectedPatient = pt;
      this.patientSearch = this.patientLabel(pt);
    },
    dobLabel(pt) {
      return formatOdDate(pt.Birthdate, 'MM/DD/YYYY');
    },
    // A method (not a computed) so "Today" is re-read every render and cannot go stale overnight.
    quickDates() {
      const fmt = m => m.format('YYYY-MM-DD');
      const daysUntilMon = ((8 - moment().day()) % 7) || 7;
      return [
        { label: 'Today', value: fmt(moment()) },
        { label: 'Tomorrow', value: fmt(moment().add(1, 'days')) },
        { label: 'Next Mon', value: fmt(moment().add(daysUntilMon, 'days')) },
        { label: 'In 1 week', value: fmt(moment().add(7, 'days')) },
      ];
    },
    readingTitle(seg) {
      if (seg.type === 'date') return 'Read as the due date. Click to change it.';
      if (seg.type === 'pat') return 'Read as a patient. Click to change the patient.';
      return this.assigneeEmp ? `Read as ${this.assigneeEmp.Name || this.assigneeEmp.Username}. Click to change.` : 'Read as a teammate. Click to choose.';
    },
    // Each detected word jumps to the control that decides it. An unresolved teammate starts the ↑/↓ walk.
    onSegmentClick(seg) {
      const go = selector => {
        this.$nextTick(() => {
          const el = this.$el.querySelector(selector);
          if (el) { el.focus(); if (el.select) el.select(); }
        });
      };
      if (seg.type === 'date') {
        go('.ss-card input[type="date"]');
      } else if (seg.type === 'pat') {
        go('.ss-card__patient-search input');
      } else if (this.navTarget && this.navTarget.kind === 'emp') {
        this.activeIndex = 0;
      } else {
        this.$nextTick(() => {
          const el = this.$el.querySelector('.ss-emp-btn--selected');
          if (el) el.focus();
        });
      }
    },
    isRecentPatient(pt) {
      return this.recents.pat.indexOf(pt.PatNum) !== -1;
    },
    // Called after a successful save with whoever the task was assigned to / about.
    rememberRecents(emp, pat) {
      const next = {
        emp: emp ? pushRecent(this.recents.emp, emp.EmployeeID, RECENT_CAP.emp) : this.recents.emp,
        pat: pat ? pushRecent(this.recents.pat, pat.PatNum, RECENT_CAP.pat) : this.recents.pat,
      };
      this.recents = next;
      try {
        window.localStorage.setItem(recentsKey(this.siteID, this.userID), JSON.stringify(next));
      } catch (e) {
        // Storage full or blocked: the list still works for this visit.
      }
    },
    empInitials(emp) {
      const parts = nameParts(emp);
      if (!parts.length) return '?';
      const first = parts[0][0];
      const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
      return (first + last).toUpperCase();
    },
    empFirstName(emp) {
      return nameParts(emp)[0] || '';
    },
    // Teammates that fit an ambiguous mention show their full name so two "Jessica"s can be told apart.
    empLabel(emp) {
      if (this.resolved.employeeCandidates.length > 1 && this.isCandidate(emp)) return nameParts(emp).join(' ');
      return this.empFirstName(emp);
    },
    // A date the user picked: the text stops deciding the due date.
    setDue(value) {
      this.dueDate = value || '';
      this.lastAutoDue = '';
    },
    // A method (not a computed) so "today" is re-read at use and cannot go stale overnight.
    todayYmd() {
      return moment().format('YYYY-MM-DD');
    },
    toggleExtra(key) {
      // The panel's focused input is about to be removed; Chrome then fires focusout with no target and the card would close.
      const panel = this.$el.querySelector('.ss-extra-panel');
      if (panel && panel.contains(document.activeElement) && this.$refs.input) this.$refs.input.focus();
      this.openExtra = this.openExtra === key ? '' : key;
      if (!this.openExtra) return;
      if (key === 'wiki') this.loadWiki();
      if (key === 'tags') this.ensureTagListener();
      if (key !== 'repeat') {
        this.$nextTick(() => {
          const el = this.$el.querySelector('.ss-extra-panel input');
          if (el) el.focus();
        });
      }
    },
    closeExtra() {
      this.openExtra = '';
      this.$nextTick(() => { if (this.$refs.input) this.$refs.input.focus(); });
    },
    onRepeatChange(value) {
      this.repeatPick = value === 'text' ? null : value;
    },
    // The site's wiki pages, read silently (no alert, no store write) the first time the picker opens.
    async loadWiki() {
      if (this.wikiState === 'loading' || this.wikiState === 'loaded') return;
      this.wikiState = 'loading';
      try {
        const res = await this.$store.dispatch('wiki/getWikiPages', { siteID: this.siteID, silent: true });
        const rows = Array.isArray(res) && Array.isArray(res[0]) ? res[0] : [];
        this.wikiPages = rows
          .filter(r => r && r.WikiPageID != null && typeof r.Name === 'string' && r.Name.trim())
          .map(r => ({ WikiPageID: r.WikiPageID, Name: r.Name }));
        this.wikiState = 'loaded';
      } catch (err) {
        console.error('ShapeShift wiki pages error:', err);
        this.wikiState = 'error';
      }
    },
    toggleWiki(id) {
      this.wikiIDs = this.wikiIDs.includes(id) ? this.wikiIDs.filter(x => x !== id) : this.wikiIDs.concat(id);
    },
    // The Tasks page normally owns the site-tags listener; start one only if nobody has.
    ensureTagListener() {
      const tasks = this.$store.state.tasks;
      if (this.ownsTagListener || (tasks && tasks.siteTaskTagsLoaded)) return;
      this.ownsTagListener = true;
      Promise.resolve(this.$store.dispatch('tasks/subscribeSiteTaskTags', { siteID: this.siteID })).catch((err) => {
        console.error('ShapeShift site tags error:', err);
      });
    },
    addTag(tag) {
      const clean = cleanTag(tag);
      this.tagQuery = '';
      if (!clean || this.tags.some(t => t.toLowerCase() === clean.toLowerCase())) return;
      this.tags = this.tags.concat(clean);
    },
    // Enter / Save: the text becomes a tag, using the site's own spelling when it already has that tag.
    commitTag() {
      const clean = cleanTag(this.tagQuery);
      if (!clean) { this.tagQuery = ''; return; }
      const exact = this.tagChoicesAll.suggestions.find(s => s.toLowerCase() === clean.toLowerCase());
      this.addTag(exact || clean);
    },
    removeTag(tag) {
      this.tags = this.tags.filter(t => t !== tag);
    },
    commitSubtask() {
      const content = this.subtaskDraft.trim();
      this.subtaskDraft = '';
      if (!content) return;
      const patch = subtasksPatch({ subtasks: this.subtasks }, { type: 'add', content, id: newSubtaskId() }, { now: new Date() });
      if (patch) this.subtasks = patch.subtasks;
    },
    removeSubtask(index) {
      const patch = subtasksPatch({ subtasks: this.subtasks }, { type: 'remove', index }, { now: new Date() });
      if (patch) this.subtasks = patch.subtasks;
    },
    resetExtras() {
      this.openExtra = '';
      this.repeatPick = null;
      this.wikiIDs = [];
      this.wikiQuery = '';
      this.tags = [];
      this.tagQuery = '';
      this.subtasks = [];
      this.subtaskDraft = '';
    },
    // Text only: focus stays where it is, so the next task typed straight after Enter still opens its card.
    reset() {
      this.resetExtras();
      this.text = '';
      this.dueDate = '';
      this.lastAutoDue = '';
      this.assigneeID = null;
      this.assigneeManual = false;
      this.selectedPatient = null;
      this.patientSearch = '';
      this.patientSearchManual = false;
      this.disambiguateChoice = null;
      this.error = '';
    },
    async create() {
      const text = this.text.trim();
      if (!text || this.saving) return;
      // Text typed in a tag / subtask box but never confirmed with Enter still counts.
      this.commitTag();
      this.commitSubtask();
      this.saving = true;
      this.error = '';
      try {
        // Embed the picks as the same badges the @ / # pickers write. buildNewTask then derives
        // assignees, assignedToEmpID, taggedPatients and the assignee notifications from the HTML,
        // exactly as the classic composer's tasks get them.
        let contentHTML = escapeText(text);
        if (this.assigneeEmp) {
          contentHTML += '&nbsp;' + buildEmployeeBadgeHTML({
            EmployeeID: this.assigneeEmp.EmployeeID,
            Name: this.assigneeEmp.Name || this.assigneeEmp.Username,
          });
        }
        if (this.selectedPatient) {
          contentHTML = ensurePatientTagged(contentHTML, {
            siteID: this.siteID,
            PatNum: this.selectedPatient.PatNum,
            LName: this.selectedPatient.LName,
            FName: this.selectedPatient.FName,
          });
        }

        const { task, notifications } = buildNewTask({
          contentHTML,
          siteID: this.siteID,
          userID: this.userID,
          userName: this.userName,
          timezone: this.timezone,
          maxSortID: this.maxSortId,
          selectedTags: this.tags,
          detectedDateInfo: this.repeatPlan && this.repeatPlan.source === 'text' ? this.textDate.recurrence : null,
          selectedDueDate: this.shownDue || null,
        });
        if (this.repeatPlan && this.repeatPlan.source === 'menu') Object.assign(task, this.repeatPlan.fields);
        // buildNewTask flags a "monthly on the Nth" task for the store's early-return save, which skips
        // the assignee notifications, the search index and tag sync. The flag is not a stored field.
        delete task._preserveRepeatOnDay;
        if (this.wikiIDs.length) task.wikiPages = this.wikiIDs.slice();
        if (this.subtasks.length) task.subtasks = this.subtasks.map(s => Object.assign({}, s));

        this.$emit('saving', task);
        const taskId = await this.$store.dispatch('tasks/addPersonalTask', { task, notification: notifications });
        this.rememberRecents(this.assigneeEmp, this.selectedPatient);
        this.$emit('saved', taskId, task);
        this.reset();
      } catch (err) {
        console.error('ShapeShift create task error:', err);
        this.error = 'Could not add the task. Please try again.';
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
// One rounded shell holds the input and the card, so the box visibly "grows" into the card.
// The accent (icon chip, focus ring, badge, selected states) follows the detected intent.
.ss-wrap {
  --ss-accent: #547fff;
  --ss-tint: rgba(84, 127, 255, 0.1);
  --ss-ink: #3b5bdb;
  --ss-ring: rgba(84, 127, 255, 0.18);
  position: relative;
  max-width: 680px;
  color: #1f2933;
  &--reminder { --ss-accent: #f59e0b; --ss-tint: #fff4dc; --ss-ink: #a85a05; --ss-ring: rgba(245, 158, 11, 0.2); }
  &--patient  { --ss-accent: #10b981; --ss-tint: #e4f7ee; --ss-ink: #047857; --ss-ring: rgba(16, 185, 129, 0.18); }
  &--generic  { --ss-accent: #64748b; --ss-tint: #eef1f5; --ss-ink: #475569; --ss-ring: rgba(100, 116, 139, 0.18); }
}

.ss-shell {
  background: #fff;
  border: 1px solid #e3e8ee;
  border-radius: 20px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04), 0 6px 20px rgba(16, 24, 40, 0.05);
  transition: border-color 0.2s, box-shadow 0.2s;
  &--active {
    border-color: var(--ss-accent);
    box-shadow: 0 0 0 4px var(--ss-ring), 0 14px 36px rgba(16, 24, 40, 0.1);
  }
}

.ss-input-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
}
.ss-icon-chip {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 38px;
  height: 38px;
  border-radius: 12px;
  background: var(--ss-tint);
  color: var(--ss-accent);
  transition: background 0.2s, color 0.2s;
}
.ss-ico {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.8;
  stroke-linecap: round;
  stroke-linejoin: round;
  &--accent { color: var(--ss-accent); }
  &--sm { width: 14px; height: 14px; }
}
.ss-input {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  font-size: 18px;
  line-height: 1.4;
  color: #1f2933;
  background: transparent;
  &::placeholder { color: #9aa5b1; }
}
.ss-intent-badge {
  flex-shrink: 0;
  padding: 4px 12px;
  border-radius: 999px;
  background: var(--ss-tint);
  color: var(--ss-ink);
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  transition: background 0.2s, color 0.2s;
}

.ss-card {
  padding: 16px 18px 14px;
  border-top: 1px solid #edf0f4;
}
.ss-card__header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  .ss-ico { width: 18px; height: 18px; }
}
.ss-reading {
  margin-bottom: 14px;
  padding: 8px 12px;
  border-radius: 10px;
  background: #f8fafc;
  color: #52606d;
  font-size: 13px;
  line-height: 1.9;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
.ss-reading__chip {
  display: inline;
  margin: 0 1px;
  padding: 1px 8px;
  border: 1px solid transparent;
  border-radius: 999px;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
  transition: box-shadow 0.12s;
  &:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(84, 127, 255, 0.3); }
  &--emp  { background: rgba(84, 127, 255, 0.12); color: #3b5bdb; &:hover { box-shadow: 0 0 0 3px rgba(84, 127, 255, 0.18); } }
  &--pat  { background: #e4f7ee; color: #047857; &:hover { box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.18); } }
  &--date { background: #fff4dc; color: #a85a05; &:hover { box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.2); } }
}
.ss-recent-tag {
  margin-left: 6px;
  padding: 0 6px;
  border-radius: 999px;
  background: #eef1f5;
  color: #52606d;
  font-size: 10px;
  font-weight: 600;
  vertical-align: middle;
}
.ss-card__title { font-weight: 600; font-size: 14px; color: #1f2933; }
.ss-card__header--secondary {
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px dashed #e3e8ee;
}
.ss-card__quick-dates {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 14px;
}
.ss-quick-btn {
  border: 1px solid #e3e8ee;
  border-radius: 999px;
  background: #fff;
  color: #475569;
  font-size: 13px;
  font-weight: 500;
  padding: 5px 14px;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
  &:hover { background: var(--ss-tint); border-color: var(--ss-accent); color: var(--ss-ink); }
  &.active {
    background: var(--ss-tint);
    border-color: var(--ss-accent);
    color: var(--ss-ink);
    font-weight: 600;
    box-shadow: 0 0 0 3px var(--ss-ring);
  }
}
.ss-card__custom-date,
.ss-card__row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
}
.ss-label { margin: 0; font-size: 12px; font-weight: 500; color: #6b7a90; white-space: nowrap; }
.ss-card .form-control {
  height: 36px;
  border: 1px solid #e3e8ee;
  border-radius: 10px;
  background: #f9fbfd;
  color: #1f2933;
  box-shadow: none;
  &:focus { background: #fff; border-color: var(--ss-accent); box-shadow: 0 0 0 3px var(--ss-ring); }
}
.ss-card__row .form-control,
.ss-card__custom-date .form-control { width: auto; min-width: 150px; }
.ss-card__patient-search .form-control { width: 100%; }

.ss-preview {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 14px;
}
.ss-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px 4px 6px;
  border-radius: 999px;
  background: var(--ss-tint);
  color: var(--ss-ink);
  font-size: 12px;
  font-weight: 600;
  .ss-ico--sm { margin-left: 6px; }
}
.ss-pill__avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: var(--ss-accent);
  color: #fff;
  font-size: 9px;
  font-weight: 700;
}

.ss-pill__x {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  margin: 0 -6px 0 0;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: inherit;
  cursor: pointer;
  opacity: 0.7;
  &:hover { opacity: 1; background: rgba(0, 0, 0, 0.08); }
  svg { width: 12px; height: 12px; fill: none; stroke: currentColor; stroke-width: 2.4; stroke-linecap: round; }
}

// The optional row shared by every card: Repeat and Attach.
.ss-extras {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}
.ss-extra-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 12px 5px 6px;
  border: 1px dashed #cfd7e2;
  border-radius: 999px;
  background: #fff;
  color: #6b7a90;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
  &:hover { border-color: var(--ss-accent); color: var(--ss-ink); }
  &--set { border-style: solid; color: var(--ss-ink); }
  &--open { background: var(--ss-tint); border-style: solid; border-color: var(--ss-accent); color: var(--ss-ink); }
}
.ss-extra-panel {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 14px;
  padding: 10px 12px;
  border: 1px solid #e3e8ee;
  border-radius: 14px;
  background: #fbfcfe;
  .ss-patient-list { margin-top: 0; }
}
.ss-sublist {
  margin: 0;
  padding: 0;
  list-style: none;
}
.ss-sublist__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 5px 2px;
  border-bottom: 1px solid #f1f3f7;
}
.ss-sublist__text { min-width: 0; font-size: 13px; overflow-wrap: anywhere; }
.ss-row__x {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: #6b7a90;
  cursor: pointer;
  &:hover { background: #eef1f5; color: #1f2933; }
  svg { width: 14px; height: 14px; fill: none; stroke: currentColor; stroke-width: 2.2; stroke-linecap: round; }
}

.ss-error { font-size: 13px; margin-bottom: 10px; }
.ss-card__footer {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-top: 12px;
  border-top: 1px solid #edf0f4;
  .btn-link {
    padding: 6px 10px;
    color: #6b7a90;
    font-size: 13px;
    text-decoration: none;
    &:hover { color: #1f2933; }
  }
  .btn-primary {
    padding: 7px 18px;
    border: none;
    border-radius: 999px;
    background: #547fff;
    font-size: 13px;
    font-weight: 600;
    box-shadow: 0 2px 8px rgba(84, 127, 255, 0.3);
    transition: background 0.15s, box-shadow 0.15s, transform 0.1s;
    &::after { content: "\21B5"; margin-left: 8px; opacity: 0.7; }
    &:hover:not(:disabled) { background: #3d6bf5; }
    &:active:not(:disabled) { transform: translateY(1px); }
    &:disabled { background: #b9c9fb; box-shadow: none; opacity: 1; &::after { content: none; } }
  }
}
.ss-card__spacer { flex: 1; }
.ss-hint {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #9aa5b1;
  kbd {
    padding: 1px 7px;
    border: 1px solid #e3e8ee;
    border-bottom-width: 2px;
    border-radius: 6px;
    background: #f9fbfd;
    color: #6b7a90;
    font-family: inherit;
    font-size: 11px;
    font-weight: 600;
    box-shadow: none;
  }
}

.ss-card__employees {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 14px;
  max-height: 132px;
  overflow-y: auto;
  scrollbar-width: thin;
}
.ss-emp-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 5px 14px 5px 5px;
  border: 1px solid #e3e8ee;
  border-radius: 999px;
  background: #fff;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, box-shadow 0.15s;
  &:hover { background: #f3f6fb; border-color: #cdd6e3; }
  &--suggested { background: #fff8e8; border-color: #f5c56b; }
  &--active { background: #f3f6fb; border-color: var(--ss-accent); box-shadow: 0 0 0 3px var(--ss-ring); }
  &--selected,
  &--selected:hover { background: var(--ss-tint); border-color: var(--ss-accent); box-shadow: 0 0 0 3px var(--ss-ring); }
}
.ss-emp-btn__avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #547fff;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
}
.ss-emp-btn__name { font-size: 13px; font-weight: 500; color: #1f2933; }

.ss-card__patient-search { margin-bottom: 14px; }
.ss-patient-list {
  margin-top: 8px;
  border: 1px solid #e3e8ee;
  border-radius: 12px;
  overflow: hidden;
  max-height: 168px;
  overflow-y: auto;
}
.ss-patient-btn {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 9px 14px;
  border: none;
  border-bottom: 1px solid #f1f3f7;
  background: #fff;
  cursor: pointer;
  text-align: left;
  transition: background 0.12s;
  &:last-child { border-bottom: none; }
  &:hover { background: #f3f6fb; }
  &--active { background: #f3f6fb; box-shadow: inset 3px 0 0 var(--ss-accent); }
  &--selected,
  &--selected:hover { background: var(--ss-tint); }
}
.ss-patient-btn__name { font-size: 13px; font-weight: 600; color: #1f2933; }
.ss-patient-btn__dob { font-size: 11px; white-space: nowrap; }
.ss-patient-empty { font-size: 13px; padding: 8px 2px; }

.ss-disambiguate-btns {
  display: flex;
  gap: 10px;
  margin-bottom: 14px;
}
.ss-disambiguate-btn {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  padding: 12px 14px;
  border: 1px solid #e3e8ee;
  border-radius: 14px;
  background: #fff;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s, border-color 0.15s, box-shadow 0.15s;
  &:hover { background: #f9fbfd; border-color: #547fff; box-shadow: 0 0 0 3px rgba(84, 127, 255, 0.12); }
}
.ss-disambiguate-btn__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 38px;
  height: 38px;
  border-radius: 12px;
  &--team { background: rgba(84, 127, 255, 0.1); color: #547fff; }
  &--patient { background: #e4f7ee; color: #10b981; }
}
.ss-disambiguate-btn__text { display: flex; flex-direction: column; min-width: 0; }
.ss-disambiguate-btn__label { font-size: 13px; font-weight: 600; color: #1f2933; }
.ss-disambiguate-btn__sub { font-size: 11px; color: #6b7a90; }

.ss-card-enter-active, .ss-card-leave-active { transition: opacity 0.18s, transform 0.18s; }
.ss-card-enter, .ss-card-leave-to { opacity: 0; transform: translateY(-6px); }

@media (max-width: 575px) {
  .ss-input-row { padding: 12px 14px; gap: 10px; }
  .ss-card { padding: 14px; }
  .ss-hint { display: none; }
  .ss-disambiguate-btns { flex-direction: column; }
  .ss-intent-badge { display: none; }
}
</style>
